import type { AssetDto } from "@models/data/assetDto";
import type {
  AnnotationDto,
  AnnotationQueryResult,
  AnnotationReplyDto,
} from "@models/data/annotationDto";
import type {
  EntitySettingsPatch,
  EntitySnapshotPatch,
} from "@models/data/entityData";
import type { SkyboxData } from "@models/data/skyboxData";
import type { EntitySettingsDto } from "@models/data/entitySettingsDto";
import type { EntitySnapshotDto } from "@models/data/entitySnapshotDto";
import type { FolderDto, FolderInfo } from "@models/data/folderDto";
import type { MaterialDto } from "@models/data/materialDto";
import type { MediaAssetDto, MediaTileDto } from "@models/data/mediaTileData";
import type { PartDto } from "@models/data/partDto";
import type { ProductDto } from "@models/data/productDto";
import type { ProjectDto } from "@models/data/projectDto";
import type { ProductDocumentDto } from "@models/data/productDocumentDto";
import type { SceneDto } from "@models/data/sceneDto";
import type { SharedLinkDto } from "@models/data/sharedLinkDto";
import type { WorkflowSummaryDto } from "@models/data/workflowSummaryDto";
import type { WorkflowProductReferenceInput } from "@models/data/workflowProductReferenceDto";
import type {
  CuttingPlaneState,
  HiddenObjectMode,
  SceneInfo,
  SceneViewState,
} from "@models/data/sceneInfo";
import type {
  SelectionSetData,
  SelectionSetUpdateFlag,
} from "@models/data/selectionSetData";

/**
 * `IStorageModel` — web port of Unity's
 * `Models/StorageModel/IStorageModel.cs` (composed of
 * `IMetadataStorageModel` + `IDataStorageModel` +
 * `ResetCloudEnvironment()`).
 *
 * The web port mirrors Unity method names verbatim so a future
 * cross-reference is mechanical:
 *   - `getProjectsByCustomer` ↔ `IMetadataStorageModel.GetProjectsByCustomer`
 *   - `getCustomerProject`    ↔ `IMetadataStorageModel.GetCustomerProject`
 *   - `getThumbnailBlob`      ↔ `IDataStorageModel.GetBytes` for thumbnails
 *
 * Real implementation:
 *   - Metadata: AWS DynamoDB `Query`, browser SDK signed with
 *     Cognito Identity-Pool credentials (mirrors Unity's
 *     `DynamoSdkclient` fallback when no `DataProxyEndpoint` is set).
 *   - Data: S3 `GetObject` under
 *     `account/{customerId}/projectdata/{storagePath}` (mirrors
 *     Unity's `AwsDataStorageModel.ObjectKeyForCustomer`).
 *
 * Offline stub (`OfflineStorageModel`) implements the same surface
 * for stub-auth dev runs (`VITE_AUTH_PROVIDER=stub`).
 */

export interface ThumbnailBlob {
  readonly sceneId: string;
  /** Object URL or remote URL — the UI doesn't care which. */
  readonly url: string;
  readonly etag: string | null;
}

export interface DocumentBytes {
  readonly bytes: Uint8Array;
  readonly contentType: string | null;
}

/**
 * Raw scene-row environment attributes as persisted by
 * `DynamoClient.UpdateSceneEnvironment` (`DynamoClient.cs:2368`).
 * `shadowsVisibleMask` carries the full per-device `ShadowMode` flags
 * (`ShadowsVisiblePerDevice` attribute).
 */
export interface SceneEnvironmentAttributes {
  readonly gridVisible: boolean;
  readonly shadowsVisibleMask: number;
  readonly consoleVisible: boolean;
  readonly tableVisible: boolean;
  readonly lightMode: number;
}

/**
 * Byte-level transfer progress for a single S3 object — the storage layer's
 * equivalent of Unity's `S3Client.CopyStream` position reporting
 * (`Models/StorageModel/AWS/S3Client.cs:93`).
 *
 * `total` is `null` when the server did not send a usable `Content-Length`,
 * so a consumer must treat the transfer as INDETERMINATE rather than dividing
 * by it. `loaded` is cumulative and monotonically increasing.
 *
 * This is the convention for every progress-reporting transport method here
 * (downloads today, uploads when `putAssetObject` grows one): a plain
 * callback in the LAST parameter position, so `signal` keeps its slot and
 * existing call sites need no edit.
 */
export type TransferProgress = (loaded: number, total: number | null) => void;

/** The three sketch layer PNGs (Unity `PathTemplates.cs:26-29`). */
export type SketchBlobKind = "sketch" | "background" | "preview";

/**
 * `storagePath` for one sketch layer — Unity's `k_sketchTemplate` /
 * `k_sketchBackgroundTemplate` / `k_sketchPreviewTemplate`. The misspelled
 * "Sketchs" folder is verbatim upstream contract; do not fix it.
 */
export function sketchStoragePath(sketchId: string, kind: SketchBlobKind): string {
  switch (kind) {
    case "background":
      return `Sketchs/${sketchId}-bg.png`;
    case "preview":
      return `Sketchs/${sketchId}-preview.png`;
    default:
      return `Sketchs/${sketchId}.png`;
  }
}

export interface IStorageModel {
  /** True if the storage layer is talking to a real backend. */
  readonly isOnline: boolean;

  // ---- Metadata --------------------------------------------------

  /**
   * Mirrors Unity's `IMetadataStorageModel.GetProjectsByCustomer`.
   * Returns the raw Dynamo row shape; the project list model maps
   * these into the UI-shaped `ProjectInfo`.
   */
  getProjectsByCustomer(
    customerId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<ProjectDto>>;

  /**
   * Mirrors `IMetadataStorageModel.GetCustomerProject`. Returns `null`
   * when the project does not exist (matches Unity's permissive
   * "no row → empty `ProjectData`" behavior, surfaced as `null` here).
   */
  getCustomerProject(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<ProjectDto | null>;

  /**
   * Mirrors `IMetadataStorageModel.GetScenes(customerId, projectId)`
   * (`DynamoClient.cs:1744` — sk `between
   * "#project:{projectId}:scene:{MinGuid}"` and
   * `"…{MaxGuid}"`). Returns the raw Dynamo row shapes; the projects
   * grid maps these into `SceneInfo` lazily.
   */
  getScenes(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<SceneDto>>;

  /**
   * Mirrors `IMetadataStorageModel.CreateScene(customerId, projectId, scene)`
   * (`DynamoClient.cs:1841` → single `PutItem` on the scene row built by
   * `ToSceneItem`, `pk = "{customerId}:project"`,
   * `sk = "#project:{projectId}:scene:{sceneId}"`). Used by
   * `ProjectModel.createSceneAsync` — both the editor "Add scene" flow and
   * the mandatory first scene created on load when a project has none
   * (Unity `ProjectModelSlim.cs:425-426`). The caller owns the full
   * `SceneInfo`; this method just serializes it to the Unity attribute
   * shape so the row round-trips in both the web and Unity clients.
   */
  createScene(
    customerId: string,
    projectId: string,
    scene: SceneInfo,
    signal?: AbortSignal,
    extraAttrs?: Readonly<Record<string, unknown>>,
  ): Promise<void>;

  /**
   * Mirrors `IMetadataStorageModel.UpdateSceneName(customerId, projectId, scene)`
   * (`DynamoClient.cs:1873` via `CreateSceneUpdateRequest`):
   *
   *     UpdateExpression = "SET #N = :new, #MT = :ts"
   *     #N = "Name", #MT = "ModifiedTimestamp"
   *
   * Unlike the older per-field scene updates here, the `ModifiedTimestamp`
   * bump is part of Unity's write — keep it so scene-list sorting and
   * thumbnail-cache keys stay coherent.
   */
  updateSceneName(
    customerId: string,
    projectId: string,
    sceneId: string,
    name: string,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }>;

  /**
   * Mirrors `IMetadataStorageModel.DeleteScene(customerId, projectId, sceneId)`
   * (`DynamoClient.cs:2571`): a single `DeleteItem` on the scene row only.
   * The per-entity / thumbnail / skybox cascade is the caller's job
   * (`ProjectModel.deleteScene`), matching Unity's layering.
   */
  deleteScene(
    customerId: string,
    projectId: string,
    sceneId: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `IMetadataStorageModel.UpdateSceneLock`
   * (`DynamoClient.cs:2541`): `SET #D = :d` with `#D = "IsLocked"` (BOOL),
   * deliberately WITHOUT a `ModifiedTimestamp` bump. Used only by the
   * scene-duplication lock/unlock bracket.
   */
  updateSceneLock(
    customerId: string,
    projectId: string,
    sceneId: string,
    isLocked: boolean,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * S3 `DeleteObject` on
   * `account/{customerId}/projectdata/{projectId}/thumbnails/{thumbnailId}`
   * (Unity `ProjectDataModel.DeleteSceneAsync` → `DeleteFile` with
   * `LogAndContinueIfNotFound`). Missing objects are tolerated.
   */
  deleteThumbnailObject(
    customerId: string,
    projectId: string,
    thumbnailId: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `IMetadataStorageModel.CreateCustomerProject(customerId, project)`
   * (`DynamoClient.cs:260` → single `PutItem` on the project row built
   * by `ToProjectItem`). Caller owns `project` and is responsible for
   * the full attribute set (UID, Name, Author, CreatorId, AccountRole,
   * timestamps, etc); this method does not invent defaults beyond what
   * Unity does.
   *
   * Note: Unity's `DynamoClient.CreateCustomerProject` overwrites
   * `Created/ModifiedTimestamp = DateTime.UtcNow` before writing — we
   * do **not** mutate the caller's DTO; instead `HttpProjectListModel`
   * stamps both timestamps at construction so unit tests stay
   * deterministic.
   */
  createCustomerProject(
    customerId: string,
    project: ProjectDto,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `IMetadataStorageModel.UpdateCustomerProjectName(customerId, project)`
   * (`DynamoClient.cs:333`):
   *
   *     UpdateExpression = "SET #N = :new, #MT = :ts"
   *     #N = "Name", #MT = "ModifiedTimestamp"
   *
   * The bumped `ModifiedTimestamp` is the caller's responsibility —
   * we forward it verbatim so unit tests stay deterministic. Returns
   * the new ISO-8601 timestamp that was written so the caller can
   * keep its in-memory `ProjectInfo` in sync.
   */
  updateProjectName(
    customerId: string,
    projectId: string,
    name: string,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }>;

  // ---- Project folders ---------------------------------------------
  //
  // Rows: pk = `"{customerId}:folder"`, sk = `"!folder:{id}"` for the folder
  // itself and `"!folder:{id}:map:{projectId}"` for each membership. See
  // `models/data/folderDto.ts` for the contract and for why ONE range query
  // returns both kinds.

  /**
   * `DynamoClient.GetFoldersByCustomer` (`DynamoClient.cs:501`) — every folder
   * for the account with its membership already joined from the map rows.
   * Mirrors Unity's single-query-then-split, including its tolerance: a folder
   * row with a blank `ID` is dropped, and a map row naming an unknown folder is
   * ignored rather than synthesizing one.
   */
  getFoldersByCustomer(
    customerId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<FolderInfo>>;

  /** `DynamoClient.CreateFolder` — full `PutItem` of the folder row only. */
  createFolder(
    customerId: string,
    folder: FolderDto,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * `DynamoClient.UpdateFolderName` (`DynamoClient.cs:568`) —
   * `UpdateExpression = "SET #N = :new"`. Note it does NOT touch `ModifiedTS`;
   * Unity stamps that on the in-memory `FolderInfo` only. Kept verbatim so a
   * rename from either client leaves the same row.
   */
  updateFolderName(
    customerId: string,
    folderId: string,
    name: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /** `DynamoClient.AddProjectsToFolder` — one batched `PutItem` per map row. */
  addProjectsToFolder(
    customerId: string,
    folderId: string,
    projectIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void>;

  /** `DynamoClient.RemoveProjectsFromFolder` — batched deletes of the map rows. */
  removeProjectsFromFolder(
    customerId: string,
    folderId: string,
    projectIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * `DynamoClient.DeleteFolder` (`DynamoClient.cs:654`) — deletes the folder
   * row, then queries and batch-deletes every one of its map rows. The
   * contained PROJECTS are untouched; deleting those is the caller's separate
   * decision (Unity `ProjectsViewModel.DeleteFolder(deleteProjectsInside)`).
   */
  deleteFolder(
    customerId: string,
    folderId: string,
    signal?: AbortSignal,
  ): Promise<void>;

  // ---- Shared links (project sharing) ------------------------------
  //
  // Row: pk = `"{customerId}:sharedlinkId"` (literal suffix), sk = link id.
  // See `models/data/sharedLinkDto.ts` for the attribute + enum contract.

  /** `DynamoClient.GetAllSharedLinks` — every link row for the account. */
  getAllSharedLinks(
    customerId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<SharedLinkDto>>;

  /**
   * `DynamoClient.GetSharedLinksByProject` — links for one project, with
   * empty-`SharedWithEmail` rows dropped (Unity does the same).
   */
  getSharedLinksByProject(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<SharedLinkDto>>;

  /** `DynamoClient.GetSharedLink` — exact pk+sk; `null` when absent. */
  getSharedLink(
    customerId: string,
    sharedLinkId: string,
    signal?: AbortSignal,
  ): Promise<SharedLinkDto | null>;

  /**
   * `DynamoClient.CreateSharedLink` — full `PutItem` (overwrite). The caller
   * supplies `link.ID`; Unity derives it deterministically from
   * project+email so a re-invite rewrites the same row.
   */
  createSharedLink(
    customerId: string,
    link: SharedLinkDto,
    signal?: AbortSignal,
  ): Promise<void>;

  /** `DynamoClient.DeleteSharedLink`. */
  deleteSharedLink(
    customerId: string,
    sharedLinkId: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /** `SET UserRole = :new` on the link row. */
  updateSharedLinkUserRole(
    customerId: string,
    sharedLinkId: string,
    userRole: number,
    signal?: AbortSignal,
  ): Promise<void>;

  /** `SET State = :new` on the link row (visited / downgrade flags). */
  updateSharedLinkState(
    customerId: string,
    sharedLinkId: string,
    state: number,
    signal?: AbortSignal,
  ): Promise<void>;

  /** `DynamoClient.UpdateSharedLinks` — denormalize a new project name. */
  updateSharedLinksProjectName(
    customerId: string,
    sharedLinkIds: ReadonlyArray<string>,
    projectName: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * `DynamoClient.UpdateCustomerProjectAccountRole` (:842):
   * `SET #R = :role, #MT = :ts` — the project-wide "all account members"
   * role. `NoAccess (4)` means invite-only.
   */
  updateCustomerProjectAccountRole(
    customerId: string,
    projectId: string,
    accountRole: number,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }>;

  // ---- Asset library ---------------------------------------------

  /**
   * Every asset row for the account (`AWSDynamoHelpers.GetAllAssets`):
   * `pk = "{c}:asset"` over the `!asset:{guid}` range, paginated. The
   * library UI filters to `AssetVersion > 0` (0 = never saved to the
   * library) and drops `HasLicensingRestrictions` rows.
   */
  getAllAssets(customerId: string, signal?: AbortSignal): Promise<ReadonlyArray<AssetDto>>;

  /**
   * Asset preview PNG (`PathTemplates.k_assetPreviewTemplate` =
   * `assetpreview/{assetId}`, extensionless). Returns a `blob:` URL the
   * caller owns, or `null` when absent — Unity likewise swallows
   * NotFound/Forbidden and keeps its placeholder sprite.
   */
  getAssetPreviewBlobUrl(
    customerId: string,
    assetId: string,
    signal?: AbortSignal,
  ): Promise<string | null>;

  /**
   * A user's profile picture (`PathTemplates.k_userPicPathTemplate` =
   * `UserProfilePics/{userId}`, extensionless). Returns a `blob:` URL the caller
   * owns, or `null` when the user has none — which is the common case, and is
   * what makes the initials fall back.
   *
   * `customerId` is Unity's **naturalAccountId** (`CustomerModel.LoadUserProfilePicAsync`
   * `:740`): the picture is downloaded from the account that owns the user, with
   * the current customer as the fallback when it is empty.
   */
  getUserProfilePicBlobUrl(
    customerId: string,
    userId: string,
    signal?: AbortSignal,
  ): Promise<string | null>;

  /** Upload an asset preview PNG (Unity renders 512×512 at import time). */
  putAssetPreviewObject(
    customerId: string,
    assetId: string,
    bytes: Uint8Array,
    signal?: AbortSignal,
  ): Promise<void>;

  /** Delete the asset ROW only (`DynamoClient.DeleteAssets`). */
  deleteAsset(customerId: string, assetId: string, signal?: AbortSignal): Promise<void>;

  /**
   * Delete an asset's parts: the rows (`DynamoClient.DeleteParts`, BatchWrite)
   * and the V2 `assets/{assetId}/parts.json` catalog. Only for a whole-asset
   * delete — the catalog goes regardless of which ids are passed.
   */
  deleteParts(
    customerId: string,
    assetId: string,
    partIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void>;

  /** S3 delete of the asset binary (`{objectKey}`); missing key tolerated. */
  deleteAssetObject(customerId: string, objectKey: string, signal?: AbortSignal): Promise<void>;

  /** S3 delete of `assetpreview/{assetId}`; missing key tolerated. */
  deleteAssetPreviewObject(
    customerId: string,
    assetId: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `DynamoClient.UpdateCustomerProjectAssetIDs` (:286): the
   * PROJECT-row `AssetIDs` list (distinct from the per-scene `AssetIDs`).
   * This is what Unity's asset library reads to decide whether an asset is
   * in use, so the web must keep it current or the library cannot tell a
   * referenced asset from an orphan. An empty list writes Dynamo NULL.
   */
  updateCustomerProjectAssetIDs(
    customerId: string,
    projectId: string,
    assetIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }>;

  /**
   * Sets/removes the project-level custom console asset and writes the complete
   * project `AssetIDs` membership in the same Dynamo update. Keeping these
   * atomic prevents the asset library from treating the active console as an
   * unreferenced asset.
   */
  updateProjectConsoleAsset(
    customerId: string,
    projectId: string,
    consoleAssetId: string | null,
    assetIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }>;

  /**
   * Mirrors `IMetadataStorageModel.UpdateCustomerProjectUserDisplaySettings`
   * (`DynamoClient.cs:954`): per-project avatar / name-tag visibility
   * (AI-925). `SET #HA = :ha, #HUT = :hut, #MT = :ts`.
   */
  updateProjectUserDisplaySettings(
    customerId: string,
    projectId: string,
    hideAvatars: boolean,
    hideUserTags: boolean,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }>;

  /**
   * Mirrors `IMetadataStorageModel.UpdateSceneIndex(customerId, projectId, scene)`
   * (`DynamoClient.cs:1829`): updates only the scene row's `Index`.
   */
  updateSceneIndex(
    customerId: string,
    projectId: string,
    sceneId: string,
    index: number,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `IMetadataStorageModel.UpdateScenePresenterNotes(customerId, projectId, scene)`.
   * Updates only `PresenterNotes` on the scene row.
   */
  updateScenePresenterNotes(
    customerId: string,
    projectId: string,
    sceneId: string,
    notes: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `IMetadataStorageModel.UpdateSceneTransitionSpeed(customerId, projectId, scene)`
   * (`DynamoClient.cs:2017`): updates only `TransitionSpeed` on the scene row.
   */
  updateSceneTransitionSpeed(
    customerId: string,
    projectId: string,
    sceneId: string,
    speed: number,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `IMetadataStorageModel.UpdateSceneIsolationMode(customerId, projectId, scene)`
   * (`DynamoClient.cs:2285`): updates only `IsolatedEntityIDs` on the scene row.
   */
  updateSceneIsolationMode(
    customerId: string,
    projectId: string,
    sceneId: string,
    isolatedEntityIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `IMetadataStorageModel.UpdateSceneHiddenMode(customerId, projectId, scene)`
   * (`DynamoClient.cs:2295`): updates only `ShowHiddenAs` on the scene row —
   * `0` = XRay, `1` = Invisible, matching Unity's `HiddenObjectMode` ordinals.
   *
   * ⚠ This one DOES touch `ModifiedTimestamp`. Unity builds it from
   * `CreateSceneUpdateRequest` (`DynamoClient.cs:1849`), whose expression is
   * `SET #N = :new, #MT = :ts` — so does {@link updateSceneName}. The three
   * siblings directly above ({@link updateScenePresenterNotes},
   * {@link updateSceneTransitionSpeed}, {@link updateSceneIsolationMode}) go
   * through that same Unity helper and yet omit `#MT` here; that is a
   * pre-existing web divergence, not the pattern to copy.
   */
  updateSceneHiddenMode(
    customerId: string,
    projectId: string,
    sceneId: string,
    mode: HiddenObjectMode,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `IMetadataStorageModel.UpdateSceneEnvironment(customerId, projectId, scene)`
   * (`DynamoClient.cs:2368`):
   *
   *     UpdateExpression = "SET #MT = :ts, #GV = :gv, #SV = :sv, #CV = :cv, #TV = :tv, #LM = :lm"
   *     #GV/"GridVisible" BOOL, #SV/"ShadowsVisiblePerDevice" N,
   *     #CV/"ConsoleVisible" BOOL, #TV/"TableVisible" BOOL, #LM/"LightMode" N
   */
  updateSceneEnvironment(
    customerId: string,
    projectId: string,
    sceneId: string,
    env: SceneEnvironmentAttributes,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `IMetadataStorageModel.UpdateSceneCuttingPlane(customerId, projectId, scene)`
   * (`DynamoClient.cs:2091`):
   *
   *     UpdateExpression = "SET #P = :p, #R = :r, #V = :v, #E = :e, #U = :u"
   *     #P/"CuttingPlanePosition" M{x,y,z}, #R/"CuttingPlaneRotation" M{x,y,z},
   *     #V/"CuttingPlaneVisible" BOOL, #E/"CuttingPlaneExist" BOOL,
   *     #U/"UsePartColor" BOOL
   *
   * Note: Unity does NOT touch `ModifiedTimestamp` in this batch — match it.
   */
  updateSceneCuttingPlane(
    customerId: string,
    projectId: string,
    sceneId: string,
    cuttingPlane: CuttingPlaneState,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `IMetadataStorageModel.UpdateSceneView` (`DynamoClient.cs:2130`):
   *
   *   UpdateExpression = "SET #SP = :sp, #SR = :sr, #VO = :vo"
   *     #SP/"SceneViewPosition"  M{x,y,z} of N
   *     #SR/"SceneViewRotation"  M{x,y,z} of N — the orbit PIVOT, not a
   *                              rotation (see `SceneViewState`)
   *     #VO/"IsViewOrthographic" BOOL
   *
   * Note: Unity touches NEITHER `ModifiedTimestamp` NOR `VersionTag` here —
   * its sibling `UpdateSceneTransform` directly below DOES bump the
   * timestamp, so the omission is deliberate, not an oversight. Match it:
   * `ModifiedTimestamp` is the thumbnail-cache key on both clients.
   */
  updateSceneView(
    customerId: string,
    projectId: string,
    sceneId: string,
    view: SceneViewState,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `IMetadataStorageModel.UpdateSceneSnapback` (`DynamoClient.cs:2398`):
   * `UpdateExpression = "SET #SB = :sb"` with `#SB = "ShouldSnapback"` (BOOL).
   * Unity does NOT bump `ModifiedTimestamp` here — match it.
   */
  updateSceneShouldSnapback(
    customerId: string,
    projectId: string,
    sceneId: string,
    shouldSnapback: boolean,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `IMetadataStorageModel.UpdateSceneSkybox(customerId, projectId, sceneId, skybox)`
   * (`DynamoClient.cs:2408`):
   *
   *     UpdateExpression = "SET #MT = :ts, #M = :m, #SC = :sc, #CC = :cc, #I = :i, #R = :r, #S = :s"
   *     SkyboxMode N, SolidColor/CustomColor M{r,g,b,a}, SkyboxIntensity N,
   *     SkyboxRotation N, SkyboxScale M{x,y}
   *
   * Texture id / format are **not** written here — see
   * {@link updateSceneSkyboxTexture} (matches Unity's split).
   */
  updateSceneSkybox(
    customerId: string,
    projectId: string,
    sceneId: string,
    skybox: SkyboxData,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `IMetadataStorageModel.UpdateSceneSkyboxTexture(...)`
   * (`DynamoClient.cs:2449`):
   *
   *     UpdateExpression = "SET #ST = :ts, #T = :tid, #F = :f"
   *     SkyboxTimestamp S, SkyboxTextureID S, SkyboxFormat S
   */
  updateSceneSkyboxTexture(
    customerId: string,
    projectId: string,
    sceneId: string,
    skybox: SkyboxData,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Downloads the custom skybox texture from S3 and returns a `blob:`
   * URL (caller owns the lifecycle). Mirrors Unity's
   * `DownloadSkyboxAsync` against
   * `account/{customerId}/projectdata/{projectId}/skyboxes/{textureId}`
   * (`PathTemplates.k_skyboxPathTemplate` — no extension on the key).
   * Returns `null` when missing.
   */
  getSkyboxBlobUrl(
    customerId: string,
    projectId: string,
    textureId: string,
    signal?: AbortSignal,
  ): Promise<string | null>;

  /**
   * Uploads a skybox texture to the same key `getSkyboxBlobUrl` reads
   * (mirrors `ProjectDataModel.SetSkyboxFileAsync` → `PutFile`).
   */
  putSkyboxObject(
    customerId: string,
    projectId: string,
    textureId: string,
    blob: Blob,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Deletes a skybox texture object (mirrors
   * `ProjectDataModel.DeleteSkyboxFileAsync` → `DeleteFile`).
   */
  deleteSkyboxObject(
    customerId: string,
    projectId: string,
    textureId: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `IMetadataStorageModel.DeleteCustomerProject(customerId, projectId)`
   * (`DynamoClient.cs:416` → single `DeleteItem` on
   * `pk = "{customerId}:project"`, `sk = "!project:{projectId}"`).
   *
   * Per Unity, this does **not** cascade scene rows or wipe the S3
   * `account/{customerId}/projectdata/{projectId}/...` prefix — those
   * cleanups happen elsewhere (per-scene cascade only when the
   * project is currently open). We match that behavior; orphan-scene
   * GC is a server-side concern.
   */
  deleteCustomerProject(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `IMetadataStorageModel.GetEntitySnapshots(customerId, projectId, sceneId, learnerCode)`
   * (`DynamoClient.cs:2652`):
   *
   *     KeyConditionExpression = "pk = :v_pk and sk between :v_start and :v_end"
   *     :v_pk    = "{customerId}:project"
   *     :v_start = "$project:{projectId}:scene:{sceneId}:snapshot:{MinGuid}"
   *     :v_end   = "$project:{projectId}:scene:{sceneId}:snapshot:{MaxGuid}"
   *
   * For ProjectSerializationVersion V2, the S3 base payload at
   * `projects/{projectId}/scenes/{sceneId}/entity-snapshots.json` is merged
   * with these Dynamo override rows. V1 and learner rows remain Dynamo-only.
   *
   * `learnerCode` is for instance/learner-overlay snapshots
   * (`U#{learnerCode}…` sk prefix). Pass `undefined` for the base
   * snapshot set; web port doesn't model instances yet.
   */
  getEntitySnapshots(
    customerId: string,
    projectId: string,
    sceneId: string,
    learnerCode?: string,
    signal?: AbortSignal,
    serializationVersion?: number,
  ): Promise<ReadonlyArray<EntitySnapshotDto>>;

  /**
   * Mirrors `IMetadataStorageModel.GetEntitySettings(customerId, projectId, sceneId)`
   * (`DynamoClient.cs:2873`).
   *
   *     :v_start = "$project:{projectId}:scene:{sceneId}:entity:{MinGuid}"
   *     :v_end   = "$project:{projectId}:scene:{sceneId}:entity:{MaxGuid}"
   */
  getEntitySettings(
    customerId: string,
    projectId: string,
    sceneId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<EntitySettingsDto>>;

  /**
   * Mirrors `IMetadataStorageModel.GetSelectionSets(customerId, projectId, sceneId)`
   * (`DynamoClient.cs:4119`).
   */
  getSelectionSets(
    customerId: string,
    projectId: string,
    sceneId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<SelectionSetData>>;

  createSelectionSet(
    customerId: string,
    projectId: string,
    sceneId: string,
    selectionSet: SelectionSetData,
    signal?: AbortSignal,
  ): Promise<void>;

  deleteSelectionSets(
    customerId: string,
    projectId: string,
    sceneId: string,
    selectionSetIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void>;

  updateSelectionSet(
    customerId: string,
    projectId: string,
    sceneId: string,
    selectionSet: SelectionSetData,
    flags: ReadonlyArray<SelectionSetUpdateFlag>,
    signal?: AbortSignal,
  ): Promise<void>;

  // ---- Media tiles / media assets (text tiles) --------------------

  /**
   * Mirrors `DynamoClient.GetMediaTiles`: Query
   * `pk = "{customerId}:project"`, sk between
   * `"$project:{projectId}:scene:{sceneId}:mediatile:{MinGuid}"` and
   * `"…{MaxGuid}"`.
   */
  getMediaTiles(
    customerId: string,
    projectId: string,
    sceneId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<MediaTileDto>>;

  /**
   * Mirrors `DynamoClient.GetMediaAssets`: sk between
   * `"$project:{projectId}:mediaasset:{MinGuid}"` and `"…{MaxGuid}"`.
   */
  getMediaAssets(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<MediaAssetDto>>;

  /** Mirrors `DynamoClient.GetMediaAsset` (single exact-sk row). */
  getMediaAsset(
    customerId: string,
    projectId: string,
    mediaAssetId: string,
    signal?: AbortSignal,
  ): Promise<MediaAssetDto | null>;

  /** Mirrors `DynamoClient.CreateMediaTile` (full-row `PutItem`). */
  createMediaTile(
    customerId: string,
    projectId: string,
    sceneId: string,
    tile: MediaTileDto,
    signal?: AbortSignal,
  ): Promise<void>;

  /** Mirrors `DynamoClient.CreateMediaAsset` (full-row `PutItem`). */
  createMediaAsset(
    customerId: string,
    projectId: string,
    asset: MediaAssetDto,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `DynamoClient.UpdateMediaTiles` — Unity persists tile edits
   * as full-row replace puts (BatchWrite), not attribute updates.
   */
  updateMediaTiles(
    customerId: string,
    projectId: string,
    sceneId: string,
    tiles: ReadonlyArray<MediaTileDto>,
    signal?: AbortSignal,
  ): Promise<void>;

  /** Mirrors `DynamoClient.DeleteMediaTiles` (key-only deletes). */
  deleteMediaTiles(
    customerId: string,
    projectId: string,
    sceneId: string,
    tileIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Deletes `mediaasset` rows. NOTE: Unity's `DeleteMediaAssets` builds
   * the sk with a doubled separator (`$project:{p}::mediaasset:{id}`,
   * upstream bug) so its deletes silently no-op and rows are orphaned;
   * the web port writes the correct key and actually deletes.
   */
  deleteMediaAssets(
    customerId: string,
    projectId: string,
    assetIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `DynamoClient.UpdateMediaAssetText`
   * (`SET Text = :t, ActionDataId = :a`).
   */
  updateMediaAssetText(
    customerId: string,
    projectId: string,
    mediaAssetId: string,
    text: string,
    actionDataId: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `DynamoClient.UpdateMediaAssetImage`. Writes `ImageId` +
   * `ModifiedTS` (Unity bumps `ModifiedTimestamp` here by mistake —
   * readers only consume `ModifiedTS`, so we write the read attribute).
   */
  updateMediaAssetImage(
    customerId: string,
    projectId: string,
    mediaAssetId: string,
    imageId: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `DynamoClient.UpdateSceneMediaAssetIDs`
   * (`DynamoClient.cs:1928`): sets the scene row's `MediaAssetIDs`
   * list (NULL when empty) + `ModifiedTimestamp`.
   */
  updateSceneMediaAssetIDs(
    customerId: string,
    projectId: string,
    sceneId: string,
    mediaAssetIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Downloads a customer-level texture (media-tile images) from
   * `account/{customerId}/projectdata/textures/{textureId}`
   * (`PathTemplates.k_texturePathTemplate`) and returns a `blob:` URL,
   * or `null` when missing. Caller owns the URL lifecycle.
   */
  getTextureBlobUrl(
    customerId: string,
    textureId: string,
    signal?: AbortSignal,
  ): Promise<string | null>;

  /** Uploads a texture object to the same key `getTextureBlobUrl` reads. */
  putTextureObject(
    customerId: string,
    textureId: string,
    blob: Blob,
    signal?: AbortSignal,
  ): Promise<void>;

  /** Deletes a texture object (media-tile image cleanup). */
  deleteTextureObject(
    customerId: string,
    textureId: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `IMetadataStorageModel.GetMaterialBatch(customerId, materialIds)`
   * (`DynamoClient.cs:1378`): reads customer-level material rows.
   */
  getMaterialBatch(
    customerId: string,
    materialIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<MaterialDto>>;

  /**
   * Mirrors `IMetadataStorageModel.UpdateMaterial(customerId, projectId, material)`
   * (`DynamoClient.cs:1450`): whole-row material upsert.
   */
  updateMaterial(
    customerId: string,
    projectId: string,
    material: MaterialDto,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `StorageModel.GetParts(customerId, serializationVersion, assetId)`.
   * V2 reads the gzip JSON catalog at `assets/{assetId}/parts.json`; V1 reads
   * the following Dynamo range and falls back to S3 when no rows exist:
   *
   *     pk = "{customerId}:asset"
   *     sk between "#asset:{assetId}:part:{MinGuid}"
   *                 and "#asset:{assetId}:part:{MaxGuid}"
   *
   * Returns the parts catalog needed to resolve
   * `EntitySnapshotDto.PartID → AssetDto.ID` for the GLB-render path.
   */
  getParts(
    customerId: string,
    assetId: string,
    signal?: AbortSignal,
    serializationVersion?: number,
  ): Promise<ReadonlyArray<PartDto>>;

  /**
   * Mirrors `IMetadataStorageModel.GetAssetById(customerId, assetId)`
   * (`DynamoClient.cs:980`):
   *
   *     pk = "{customerId}:asset"
   *     sk = "!asset:{assetId}"
   *
   * Returns `null` when the asset row is missing.
   */
  getAssetById(
    customerId: string,
    assetId: string,
    signal?: AbortSignal,
  ): Promise<AssetDto | null>;

  // ---- Model import writes (Add Model) ---------------------------

  /**
   * Upload raw asset bytes (the imported GLB) to S3 at
   * `account/{customerId}/projectdata/{objectKey}` — the inverse of
   * {@link getAssetBlobUrl}. Mirrors `AwsDataStorageModel.PutFile`.
   */
  putAssetObject(
    customerId: string,
    objectKey: string,
    blob: Blob,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Create the asset metadata row (`PutItem`, `pk = "{c}:asset"`,
   * `sk = "!asset:{asset.ID}"`). Mirrors `DynamoClient.CreateAsset`
   * → `ToAssetItem` (`DynamoClient.cs:1049`).
   */
  createAsset(customerId: string, asset: AssetDto, signal?: AbortSignal): Promise<void>;

  /**
   * Create the part rows for an asset (`sk = "#asset:{assetId}:part:{ID}"`).
   * Mirrors `DynamoClient.CreateParts` → `ToPartItem` (`DynamoClient.cs:1194`).
   * Batched (BatchWriteItem) like Unity.
   *
   * `serializationVersion >= 2` instead replaces the whole gzip catalog at
   * `assets/{assetId}/parts.json` (Unity `StorageModel.CreateParts`). The
   * version is the PROJECT's; an omitted one is V1.
   */
  createParts(
    customerId: string,
    assetId: string,
    parts: ReadonlyArray<PartDto>,
    signal?: AbortSignal,
    serializationVersion?: number,
  ): Promise<void>;

  /**
   * Whole-row asset upsert. Mirrors `DynamoClient.UpdateAsset`
   * (`DynamoClient.cs:1078`), which is literally
   * `return CreateAsset(customerId, asset)` — Dynamo `PutItem` replaces the
   * item at the same pk/sk, and Campfire always writes whole objects rather
   * than sparse attribute updates. Kept as its own name so the Save-to-Library
   * call site reads like the Unity source it ports.
   */
  updateAsset(customerId: string, asset: AssetDto, signal?: AbortSignal): Promise<void>;

  /**
   * Whole-row part upsert for an asset. Mirrors `DynamoClient.UpdateParts`
   * (`DynamoClient.cs:1234`) — the same `ToPartItem` BatchWrite as
   * {@link createParts}, separately named for the same reason as
   * {@link updateAsset}.
   */
  updateParts(
    customerId: string,
    assetId: string,
    parts: ReadonlyArray<PartDto>,
    signal?: AbortSignal,
    serializationVersion?: number,
  ): Promise<void>;

  /**
   * Create customer-level material rows in bulk (`pk = "{c}:material"`,
   * `sk = "$material:{ID}"`). Mirrors `DynamoClient.CreateMaterials`
   * → `ToMaterialItem` (`DynamoClient.cs:1489`), batched like Unity.
   * {@link updateMaterial} is the single-row equivalent.
   */
  createMaterials(
    customerId: string,
    materials: ReadonlyArray<MaterialDto>,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Delete customer-level material rows by id — the rollback of
   * {@link createMaterials} for an import that failed after writing them.
   * Mirrors `DynamoClient.DeleteMaterials` (`DynamoClient.cs:1508`).
   */
  deleteMaterials(
    customerId: string,
    materialIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Create entity-snapshot rows (`sk =
   * "$project:{p}:scene:{s}:snapshot:{SnapshotID}"`). Mirrors
   * `DynamoClient.CreateEntitySnapshots` → `ToEntitySnapshotItem`
   * (`DynamoClient.cs:2772`). Batched like Unity.
   *
   * `serializationVersion >= 2` instead merges the rows into the scene's base
   * file, `projects/{p}/scenes/{s}/entity-snapshots.json` (Unity
   * `StorageModel.QueueEntitySnapshotFileChanges`): an existing id is
   * replaced in place, a new one appended. Edits after creation stay Dynamo
   * overrides ({@link updateEntitySnapshots}) for every version.
   */
  createEntitySnapshots(
    customerId: string,
    projectId: string,
    sceneId: string,
    snapshots: ReadonlyArray<EntitySnapshotDto>,
    signal?: AbortSignal,
    serializationVersion?: number,
  ): Promise<void>;

  /**
   * Create the entity-settings row (`sk =
   * "$project:{p}:scene:{s}:entity:{EntityID}"`). Mirrors
   * `DynamoClient.CreateEntitySettings` → `ToEntitySettingsItem`
   * (`DynamoClient.cs:2949`).
   */
  createEntitySettings(
    customerId: string,
    projectId: string,
    sceneId: string,
    settings: EntitySettingsDto,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Append the scene row's `AssetIDs` list (`UpdateItem`, `sk =
   * "#project:{p}:scene:{s}"`, `SET AssetIDs, ModifiedTimestamp,
   * VersionTag`). Mirrors `DynamoClient.UpdateSceneAssetIDs`
   * (`DynamoClient.cs:1926`) — the scene-level `AssetIDs` is the source
   * of truth both the web and Unity load paths read to seed the parts
   * catalog, so the imported asset must land here.
   */
  updateSceneAssetIDs(
    customerId: string,
    projectId: string,
    sceneId: string,
    assetIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Persist snapshot patches in bounded DynamoDB transactions so one logical
   * multi-part edit does not fan out into one HTTP request per part. A
   * one-element update uses a direct DynamoDB update rather than a transaction.
   *
   * On a V2 project (`serializationVersion` ≥ 2) each patch is instead written
   * as a WHOLE override row (file row, then any existing override, then the
   * patch), because Unity's `MergeEntitySnapshots` replaces the file row with
   * the override outright — a sparse patch row reads there as a snapshot with
   * no entity, part or hierarchy.
   */
  updateEntitySnapshots(
    customerId: string,
    projectId: string,
    sceneId: string,
    updates: ReadonlyArray<{
      readonly snapshotId: string;
      readonly patch: EntitySnapshotPatch;
    }>,
    signal?: AbortSignal,
    serializationVersion?: number,
  ): Promise<void>;

  /** Persist editable fields on one `EntitySettingsData` row. */
  updateEntitySettings(
    customerId: string,
    projectId: string,
    sceneId: string,
    entityId: string,
    patch: EntitySettingsPatch,
    signal?: AbortSignal,
  ): Promise<void>;

  /** Delete an entity settings row and all known snapshot rows for it. */
  deleteEntity(
    customerId: string,
    projectId: string,
    sceneId: string,
    entityId: string,
    snapshotIds: ReadonlyArray<string>,
    signal?: AbortSignal,
    /** `>= 2` also removes the snapshots from the scene's `entity-snapshots.json`, before the rows. */
    serializationVersion?: number,
  ): Promise<void>;

  // ---- Annotations (comments) ------------------------------------
  // Mirrors `DynamoClient` annotation methods (:3269-3790). All rows share
  // `pk = "{customerId}:project"`; the base sort key (no `U#{learnerCode}`
  // instance prefix — the web port doesn't model instances) carries the ids.

  /**
   * Mirrors `DynamoClient.GetAnnotations` (:3431). One range query over the
   * `annotation:{MinGuid}`..`{MaxGuid}` window returns BOTH annotation rows
   * and their reply rows (replies sort within that window); the transport
   * splits them by the `:reply:` sk segment and tags each reply with its
   * parent annotation id parsed from the sk.
   */
  getAnnotations(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<AnnotationQueryResult>;

  /**
   * Mirrors `DynamoClient.CreateAnnotation` (:3485) → `ToAnnotationItem` full
   * `PutItem`. Detached text comments omit the position maps + list attrs.
   */
  createAnnotation(
    customerId: string,
    projectId: string,
    annotation: AnnotationDto,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `DynamoClient.UpdateAnnotationText` (:3517):
   * `SET AnnotationText = :new, ModifiedTimestamp = :mt`.
   */
  updateAnnotationText(
    customerId: string,
    projectId: string,
    annotationId: string,
    text: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `DynamoClient.ResolveAnnotation` (:3592):
   * `SET IsResolved = :new (N), ModifiedTimestamp = :mt`. `resolvedStatus`
   * is the integer written verbatim (0 = open, 1 = resolved).
   */
  resolveAnnotation(
    customerId: string,
    projectId: string,
    annotationId: string,
    resolvedStatus: number,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `DynamoClient.DeleteAnnotations` (:3608, batched key-only deletes).
   * Unity does NOT cascade reply rows here (orphans possible); the web port
   * sweeps replies separately via {@link deleteAnnotationReplies}.
   */
  deleteAnnotations(
    customerId: string,
    projectId: string,
    annotationIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors Unity's `UpdateAnnotationFlags.PlacementPose` persistence
   * (`UpdateAnnotationPlacementPosition`:3532 + `...Rotation`:3547): SET
   * PlacementPosition (M) + PlacementRotation (M) + ModifiedTimestamp. Used
   * on marker drag; never touches the anchor (AttachmentPosition).
   */
  updateAnnotationPlacement(
    customerId: string,
    projectId: string,
    annotationId: string,
    placementPosition: { x: number; y: number; z: number },
    placementRotation: { x: number; y: number; z: number; w: number },
    signal?: AbortSignal,
  ): Promise<void>;

  /** Mirrors `DynamoClient.CreateAnnotationReply` (:3690, full-row `PutItem`). */
  createAnnotationReply(
    customerId: string,
    projectId: string,
    annotationId: string,
    reply: AnnotationReplyDto,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `DynamoClient.UpdateAnnotationReplyText` (:3697). NOTE: Unity's
   * writer has a bug — it `SET`s `ModifiedTimestamp` while the reply row is
   * read via `ModifiedTS`, so the visible mod-time never changes. The web port
   * writes `ModifiedTS` so the edit's mod-time actually updates for both web
   * and Unity readers.
   */
  updateAnnotationReplyText(
    customerId: string,
    projectId: string,
    annotationId: string,
    replyId: string,
    message: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /** Mirrors `DynamoClient.DeleteAnnotationReplies` (:3732, batched deletes). */
  deleteAnnotationReplies(
    customerId: string,
    projectId: string,
    annotationId: string,
    replyIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Mirrors `DynamoClient.UpdateAnnotationSketchUploaded` (:3584):
   * `SET SketchUploaded = :new (BOOL), ModifiedTimestamp = :mt`. The sketch
   * validity gate — created rows are chased with `false` (readers default an
   * absent attribute to TRUE), and the finished editor flips it `true`.
   * Learner-code (`U#{code}` sk prefix) instances are not modeled on web.
   */
  updateAnnotationSketchUploaded(
    customerId: string,
    projectId: string,
    annotationId: string,
    sketchUploaded: boolean,
    signal?: AbortSignal,
  ): Promise<void>;

  // ---- Sketch blobs (S3) -------------------------------------------
  // Unity `PathTemplates.cs:26-29` — the folder name "Sketchs" is misspelled
  // upstream and load-bearing; keys are
  //   `account/{customerId}/projectdata/Sketchs/{sketchId}.png`      (strokes)
  //   `account/{customerId}/projectdata/Sketchs/{sketchId}-bg.png`   (screenshot)
  //   `account/{customerId}/projectdata/Sketchs/{sketchId}-preview.png` (blend)
  // Delete is intentionally absent: Unity never cleans sketch PNGs (duplication
  // and resolve/reopen flows rely on the orphans staying put).

  /**
   * Fetch one sketch layer PNG as a `blob:` URL the caller owns (revoke when
   * done). Missing objects (old-format sketches have no `-bg.png`) return
   * `null`, mirroring Unity's `LogAndContinueIfNotFound`.
   */
  getSketchBlobUrl(
    customerId: string,
    sketchId: string,
    kind: SketchBlobKind,
    signal?: AbortSignal,
  ): Promise<string | null>;

  /** Upload one sketch layer PNG (body must be a `Uint8Array` — see `putAssetObject`). */
  putSketchObject(
    customerId: string,
    sketchId: string,
    kind: SketchBlobKind,
    bytes: Uint8Array,
    signal?: AbortSignal,
  ): Promise<void>;

  // ---- Data ------------------------------------------------------

  /**
   * Fetches the thumbnail PNG bytes for a single scene from S3 and
   * returns them as a `blob:` URL ready to drop into `<img src>` /
   * `background-image: url(...)`. Mirrors the Unity download path
   * `AwsDataStorageModel.GetFile` against
   * `account/{customerId}/projectdata/{projectId}/thumbnails/{thumbnailId}`.
   *
   * Returns `null` when the object is missing (Unity treats 404 the
   * same way and falls back to the legacy raw-`sceneId` key — that
   * fallback is the caller's responsibility, not the transport's).
   *
   * The returned URL is owned by the caller; revoke it via
   * `URL.revokeObjectURL(...)` when no longer needed.
   */
  getProjectThumbnailBlobUrl(
    customerId: string,
    projectId: string,
    thumbnailId: string,
    signal?: AbortSignal,
  ): Promise<string | null>;

  /**
   * Fetch the raw asset bytes (GLB / GLTF zip / etc) from S3 and
   * return a `blob:` URL the caller can hand to the Three.js GLTF loader
   * asset loader. Mirrors the Unity
   * download path: `AwsDataStorageModel.GetFile(customerId,
   * GetAssetVariantObjectKey(asset), …)` against
   * `account/{customerId}/projectdata/{objectKey}`.
   *
   * Returns `null` when the object is missing (404 / NoSuchKey / 403).
   * The returned URL is owned by the caller; revoke via
   * `URL.revokeObjectURL(...)` when done. Variant prefix
   * (`AssetData.GetVariantKey`) — original-quality only for now;
   * mobile/low-res LODs land later.
   *
   * `onProgress` reports the transfer byte-by-byte as the body streams
   * (see {@link TransferProgress}). Scene assets are routinely hundreds of
   * megabytes, and settled-placement counting alone leaves the loading bar
   * frozen for the whole download.
   */
  getAssetBlobUrl(
    customerId: string,
    objectKey: string,
    signal?: AbortSignal,
    onProgress?: TransferProgress,
  ): Promise<{ url: string; contentType: string | null } | null>;

  getThumbnailAsync(
    sceneId: string,
    signal?: AbortSignal,
  ): Promise<ThumbnailBlob | null>;

  /**
   * Uploads a captured scene thumbnail PNG. Key template matches the read
   * path exactly: `account/{c}/projectdata/{p}/thumbnails/{thumbnailId}` with
   * NO `.png` suffix (Unity adds it only for its local cache file).
   * `thumbnailId` comes from `generateThumbnailId(sceneId)` — on plain
   * projects that IS the scene id; Unity never mints or rotates it, and the
   * scene row's vestigial `ThumbnailID` attribute is not touched. Cache
   * invalidation rides the scene-row `ModifiedTimestamp` bump below.
   */
  putThumbnailObject(
    customerId: string,
    projectId: string,
    thumbnailId: string,
    bytes: Uint8Array,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Bumps the scene row's `ModifiedTimestamp` (`DynamoClient.cs:2267`,
   * `SET #MT = :ts`). Thumbnail caches key on it, so this IS the
   * invalidation signal for scene cards.
   */
  updateSceneModifiedTimestamp(
    customerId: string,
    projectId: string,
    sceneId: string,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }>;

  /**
   * Bumps the project row's `ModifiedTimestamp` (`DynamoClient.cs:407`) so
   * the projects grid re-sorts after an edit.
   */
  updateCustomerProjectModifiedDate(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }>;

  // ---- Workflow document -----------------------------------------

  /**
   * Fetches the per-project guided-training workflow document
   * (`ProjectWorkflowModel`) — the pure-YAML `workflow.yaml`, full S3 key
   * `account/{customerId}/projectdata/{projectId}/workflow.yaml`.
   * Returns `null` when the object is MISSING (404/403/NoSuchKey); THROWS on
   * any other failure — a transient error must never read as "no workflow",
   * or a later create path template-overwrites a real cloud file.
   */
  getWorkflowYaml(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<string | null>;

  /**
   * Uploads the per-project workflow YAML to the same key `getWorkflowYaml`
   * reads. UTF-8, no BOM.
   */
  putWorkflowYaml(
    customerId: string,
    projectId: string,
    text: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Replaces the searchable DynamoDB projection derived from `workflow.yaml`.
   * The YAML remains authoritative; this is an idempotent full-row PutItem.
   */
  putWorkflowSummary(
    customerId: string,
    summary: WorkflowSummaryDto,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Deletes the per-project `workflow.yaml` object — the authoritative half of
   * "this project has no workflow". Tolerates a missing key: the panel's Delete
   * is reachable while the document is still only in memory (the save debounce
   * has not fired), and refusing there would leave the model and the cloud
   * disagreeing about a file that does not exist.
   */
  deleteWorkflowYaml(
    customerId: string,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Deletes the searchable DynamoDB projection for one workflow id, the
   * counterpart to {@link putWorkflowSummary}. Addressed by `workflow_id`
   * rather than by project, because that is the row's sort key — the summary
   * contract carries no project id (it mirrors Unity's `WorkflowSummary`
   * field-for-field).
   */
  deleteWorkflowSummary(
    customerId: string,
    workflowId: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Refreshes the product-to-workflow lookup row produced by validating
   * `workflow.product`. `firstReferencedAt` is preserved on an existing row and
   * `lastValidatedAt` is updated on every write.
   */
  putWorkflowProductReference(
    customerId: string,
    reference: WorkflowProductReferenceInput,
    signal?: AbortSignal,
  ): Promise<void>;

  /** Deletes one product-to-workflow lookup row. */
  deleteWorkflowProductReference(
    customerId: string,
    productNameKey: string,
    workflowId: string,
    signal?: AbortSignal,
  ): Promise<void>;

  // ---- Documents (library docs + AI memory) -----------------------
  //
  // The workflow-product read path is Dynamo-backed (`documentByProduct`
  // link rows plus canonical `document` rows). The remaining S3 manifest
  // methods are the retired upload/delete transport kept only for the
  // unported management surface.

  /**
   * Product-document lookup in `projectstore-metadata`: Query
   * `pk = "{customerId}:documentByProduct:{normalizedProductName}"`, then
   * BatchGet each canonical document row named by the link's `documentPk` /
   * `documentSk`.
   */
  listDocumentsByProductName(
    customerId: string,
    productName: string,
    options?: { readonly includeArchived?: boolean },
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<ProductDocumentDto>>;

  /**
   * Canonical document lookup in `projectstore-metadata`: BatchGet
   * `pk = "{customerId}:document"`, `sk = "!document:{normalizedDocumentNameKey}"`
   * for every key. Used by `workflow.knowledge.include`, where the workflow
   * names documents directly instead of discovering them through a product link.
   */
  getDocumentsByNameKeys(
    customerId: string,
    documentNameKeys: ReadonlyArray<string>,
    options?: { readonly includeArchived?: boolean },
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<ProductDocumentDto>>;

  /**
   * Canonical workflow-product row:
   * `pk = "{customerId}:product"`, `sk = "!product:{normalizedProductName}"`.
   * Returns archived rows too; callers decide whether archived is valid.
   */
  getProductByName(
    customerId: string,
    productName: string,
    signal?: AbortSignal,
  ): Promise<ProductDto | null>;

  /**
   * The account's document manifest (`docs/index.json`), raw JSON.
   * Returns `null` when the object is MISSING (404/403/NoSuchKey); THROWS on
   * any other failure — same rule as `getWorkflowYaml`, and for the same
   * reason: a transient error read as "no manifest" would let the next
   * upload write a manifest that DROPS every existing doc.
   */
  getDocsManifestJson(customerId: string, signal?: AbortSignal): Promise<string | null>;

  /** Uploads the manifest to the key `getDocsManifestJson` reads. UTF-8, no BOM. */
  putDocsManifestJson(customerId: string, json: string, signal?: AbortSignal): Promise<void>;

  /**
   * Uploads a document binary at the customer-scoped `storagePath`
   * (`docs/{fileName}`). `bytes` must be a `Uint8Array`, never a `Blob` —
   * the browser S3 client routes Blob bodies through aws-chunked streaming,
   * which throws (see `putAssetObject`).
   */
  putDocObject(
    customerId: string,
    storagePath: string,
    bytes: Uint8Array,
    contentType: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * A document's bytes as a `blob:` URL the caller owns (and must revoke),
   * or `null` when the object is absent. Used to open / download a doc.
   */
  getDocBlobUrl(
    customerId: string,
    storagePath: string,
    contentType?: string,
    signal?: AbortSignal,
  ): Promise<string | null>;

  /**
   * A document's raw bytes, for AI/vector-store indexing. Use this instead of
   * `getDocBlobUrl` whenever no DOM URL is needed.
   */
  getDocBytes(
    customerId: string,
    storagePath: string,
    signal?: AbortSignal,
  ): Promise<DocumentBytes | null>;

  /** S3 delete of a document; missing key tolerated (Unity treats it as success). */
  deleteDocObject(customerId: string, storagePath: string, signal?: AbortSignal): Promise<void>;

  /**
   * A PDF's first-page thumbnail (`docs/thumbnails/{fileName}.png`) as a
   * `blob:` URL, or `null`. Unity renders these at upload time with its
   * bundled PDF renderer; the web only READS them (see the deviation note
   * in `ProjectDocsModel`).
   */
  getDocThumbnailBlobUrl(
    customerId: string,
    fileName: string,
    signal?: AbortSignal,
  ): Promise<string | null>;

  /** S3 delete of a PDF thumbnail; missing key tolerated. */
  deleteDocThumbnailObject(
    customerId: string,
    fileName: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * The customer's persisted OpenAI vector-store id
   * (`vectorstore/vector_store.json`, Unity
   * `CustomerVectorStoreModel.VectorStoreSettingsStoragePath`). Same
   * missing-vs-failed contract as the manifest. Shared with the Unity client,
   * so both platforms index into ONE store per account.
   */
  getVectorStoreSettingsJson(customerId: string, signal?: AbortSignal): Promise<string | null>;

  putVectorStoreSettingsJson(
    customerId: string,
    json: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Web-only index of OpenAI vector-store files attached for one workflow.
   *
   * The OpenAI file-list endpoint is customer-wide and cannot filter by
   * `workflow_id`, so workflow sync reads this scoped manifest instead of
   * listing every file in the customer's vector store. Same missing-vs-failed
   * contract as `getVectorStoreSettingsJson`.
   */
  getWorkflowVectorManifestJson(
    customerId: string,
    workflowId: string,
    signal?: AbortSignal,
  ): Promise<string | null>;

  putWorkflowVectorManifestJson(
    customerId: string,
    workflowId: string,
    json: string,
    signal?: AbortSignal,
  ): Promise<void>;

  // ---- Media library (workflow step media) -------------------------
  //
  // Customer-scoped like the documents above, and for the same reason: media is
  // uploaded once per account and referenced by URL from any project's
  // `workflow.yaml`. Key shapes live in `models/data/mediaLibraryDto.ts`.

  /**
   * The account's media manifest (`media/index.json`), raw JSON. Same
   * missing-vs-failed contract as `getDocsManifestJson`: `null` only for a
   * genuinely absent object, THROW on anything else, because every write is a
   * read-modify-write against this one key.
   */
  getMediaLibraryManifestJson(customerId: string, signal?: AbortSignal): Promise<string | null>;

  /** Uploads the manifest to the key `getMediaLibraryManifestJson` reads. UTF-8, no BOM. */
  putMediaLibraryManifestJson(
    customerId: string,
    json: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * Uploads a media binary at the customer-scoped `storagePath`
   * (`media/{fileName}`). `bytes` must be a `Uint8Array`, never a `Blob` — see
   * `putAssetObject`. The `contentType` is load-bearing here rather than
   * cosmetic: the bytes come back through a `blob:` URL, and a `<video>` handed
   * `application/octet-stream` refuses to play.
   */
  putMediaLibraryObject(
    customerId: string,
    storagePath: string,
    bytes: Uint8Array,
    contentType: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /**
   * A media file's bytes as a `blob:` URL, or `null` when the object is absent.
   * The MODEL owns the returned URL's lifetime (it caches them for the tab's
   * life, the way `thumbnailCache` does) — a caller must not revoke it.
   */
  getMediaLibraryBlobUrl(
    customerId: string,
    storagePath: string,
    contentType?: string,
    signal?: AbortSignal,
  ): Promise<string | null>;

  /** S3 delete of a media file; missing key tolerated. */
  deleteMediaLibraryObject(
    customerId: string,
    storagePath: string,
    signal?: AbortSignal,
  ): Promise<void>;

  // ---- Shared application settings --------------------------------

  // NOTE: `get/putAppSettingsJson` and `get/putAgentPromptsJson` used to live
  // here. They addressed `account/{customerId}/projectdata/settings/…`, which was
  // the ONLY tree a browser can write — and that, not any property of the data,
  // is why the deployment's agent configuration was stored per customer. It is
  // not customer data: two accounts on one deployment must not run two different
  // agents. Both documents moved to the host that serves the app, behind
  // `IDeploymentConfigModel` (`models/config/`, served by
  // `deploy/config-service/`). Do not re-add a customer-scoped path for them.

  /**
   * Mirrors `DynamoClient.UpdateCustomerProjectDocumentStoragePaths` (:318):
   * the PROJECT-row `DocumentStoragePaths` list — the project↔doc join both
   * clients read to decide which of the account's documents belong to this
   * project. `SET #DSP = :new, #MT = :ts`; an empty list writes Dynamo NULL,
   * matching the `AssetIDs` writer.
   */
  updateCustomerProjectDocumentStoragePaths(
    customerId: string,
    projectId: string,
    storagePaths: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<{ modifiedAt: string }>;

  // ---- Lifecycle -------------------------------------------------

  /** Reset cached cloud handles when the user signs out / switches env. */
  resetCloudEnvironment(): void;
}

export const STORAGE_MODEL = Symbol.for("Campfire.StorageModel");
